import { create, StoreApi, UseBoundStore } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { Mutate } from 'zustand/vanilla'
import { SortDirection, ValidityFilter } from './types'

// Define the shape of a single query
export interface SheetQuery {
  id: string
  queryField: string
  queryOperation: string
  queryValues: string[]
  joinPrefix?: 'and' | 'or'
}

// Define the shape of filters for a single sheet
export interface SheetFilterState {
  sortDirection?: SortDirection
  sortField?: string
  filter?: ValidityFilter
  filterField?: string
  searchValue?: string
  searchField?: string
  q?: string
}

// Define the shape of active filter state for a single sheet
export interface SheetQueryCollectionState {
  queryCollections: Record<string, QueryCollection>
  activeQueryCollectionId?: string
}

export interface SheetQueriesState {
  activeQueryId: string | undefined
  activeViewId: string | undefined
}

// Define the shape of a query set
export interface QueryCollection {
  id: string
  type: 'atomic' | 'advanced' | 'global'
  queries: SheetQuery[]
}

// Define the shape of the entire store
interface SheetStoreState {
  filters: Record<string, SheetFilterState>
  getFilters: (sheetId: string) => SheetFilterState
  getQueries: (sheetId: string) => SheetQueriesState
  setFilter: (sheetId: string, filter: ValidityFilter | undefined) => void
  setFilterField: (sheetId: string, filterField: string | undefined) => void
  setSearchValue: (sheetId: string, searchValue: string | undefined) => void
  setSearchField: (sheetId: string, searchField: string | undefined) => void
  setQ: (sheetId: string, q: string | undefined) => void
  setSortDirection: (
    sheetId: string,
    sortDirection: SortDirection | undefined
  ) => void
  setSortField: (sheetId: string, sortField: string | undefined) => void
  updateFilters: (
    sheetId: string,
    partialFilters: Partial<SheetFilterState>
  ) => void
  resetFilters: (sheetId: string) => void
  sheetQueryCollections: Record<string, SheetQueryCollectionState>
  getQueryCollectionState: (sheetId: string) => SheetQueryCollectionState
  setQueryCollectionState: (
    sheetId: string,
    queryCollectionState: SheetQueryCollectionState
  ) => void
  createQueryCollection: (
    sheetId: string,
    query: Omit<SheetQuery, 'id'> | undefined,
    type: 'atomic' | 'advanced' | 'global'
  ) => QueryCollection
  updateQueryCollection: (
    sheetId: string,
    collectionId: string,
    updatedCollection: Partial<QueryCollection>
  ) => void
  removeQueryCollection: (sheetId: string, collectionId: string) => void
  clearQueryCollections: (sheetId: string) => void
  setActiveQueryCollection: (
    sheetId: string,
    collectionId: string | undefined
  ) => void
  setActiveQuery: (sheetId: string, queryId: string | undefined) => void
  setActiveView: (sheetId: string, viewId: string | undefined) => void
  addQueryCollectionQuery: (
    sheetId: string,
    collectionId: string,
    query: Omit<SheetQuery, 'id'>
  ) => SheetQuery
  updateQueryCollectionQuery: (
    sheetId: string,
    collectionId: string,
    query: SheetQuery
  ) => void
  removeQueryCollectionQuery: (
    sheetId: string,
    collectionId: string,
    queryId: string
  ) => void
  initSheet: (sheetId: string) => void
  queries: Record<string, SheetQueriesState>
}

// Default filter state for a new sheet
export const DEFAULT_FILTER_STATE: SheetFilterState = {
  sortDirection: undefined,
  sortField: undefined,
  filter: undefined,
  filterField: undefined,
  searchValue: undefined,
  searchField: undefined,
  q: undefined,
}

// Default active filter state for a new sheet
export const DEFAULT_QUERY_COLLECTION_STATE: SheetQueryCollectionState = {
  queryCollections: {},
  activeQueryCollectionId: undefined,
}

export const DEFAULT_QUERIES_STATE: SheetQueriesState = {
  activeQueryId: undefined,
  activeViewId: undefined,
}

// Helper to ensure a sheet exists in the state or initialize it
const ensureSheetExists = (state: any, sheetId: string) => {
  if (!state.filters) {
    state.filters = {}
  }
  if (!state.queries) {
    state.queries = {}
  }
  if (!state.filters[sheetId]) {
    state.filters[sheetId] = { ...DEFAULT_FILTER_STATE }
  }
  if (!state.sheetQueryCollections[sheetId]) {
    state.sheetQueryCollections[sheetId] = { ...DEFAULT_QUERY_COLLECTION_STATE }
  }
  if (!state.queries[sheetId]) {
    state.queries[sheetId] = { ...DEFAULT_QUERIES_STATE }
  }
}

const formatQueryOperation = (operation: string): string => {
  if (operation === 'is empty') {
    return 'eq'
  }
  if (operation === 'not empty') {
    return 'ne'
  }
  if (operation === 'is error' || operation === 'is valid') {
    return 'is'
  }
  return operation
}

/**
 * Utility which takes a string and adds "wrapping" single quotes and escapes any inner quotes
 * */
export function escapeQuotes(str: string): string {
  return `'${str.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
}

// Helper to format query value based on operation
const formatQueryValue = (operation: string, value: string): string => {
  if (operation === 'ilike') {
    return escapeQuotes(`%${value}%`)
  }

  return escapeQuotes(value)
}

const formatQueryField = (fieldValue: string): string => {
  const escapedValue = escapeQuotes(fieldValue)

  return escapedValue
}

const buildQueryStringForValue = (query: SheetQuery, value: string): string => {
  const formattedOperation = formatQueryOperation(query.queryOperation)
  const formattedValue = formatQueryValue(formattedOperation, value)
  const formattedField = formatQueryField(query.queryField)

  return `${formattedField} ${formattedOperation} ${formattedValue}`
}

const convertQueriesToQ = (
  queries: SheetQuery[],
  type: 'atomic' | 'advanced'
): string | undefined => {
  const validQueries = queries.filter((query) => query.queryValues.length > 0)
  if (validQueries.length === 0) return undefined

  let joinedQueries = ''

  validQueries.forEach((query, index) => {
    const nextQueryIndex = index + 1
    let queryJoinValue = ''

    if (nextQueryIndex <= validQueries.length - 1) {
      const nextQuery = validQueries[nextQueryIndex]
      queryJoinValue = nextQuery.joinPrefix ? `${nextQuery.joinPrefix} ` : 'and'
    }

    const queryStringsForValue = query.queryValues.map((value) => {
      const queryString = buildQueryStringForValue(query, value)
      return queryString
    })

    let queryGroup = `${queryStringsForValue.join(' or ')}`

    // Queries within an advanced collection should be grouped together so we can
    // differentiate between a query w/ many values vs individual queries for different values
    if (type === 'advanced') {
      queryGroup = `(${queryGroup})`
    }

    // Join the query group with the next query
    if (queryJoinValue) {
      joinedQueries += `${queryGroup} ${queryJoinValue}`
    } else {
      joinedQueries += queryGroup
    }
  })

  return type === 'atomic' ? joinedQueries : `(${joinedQueries})`
}

/**
 * Convert a collection of queries to a q string
 *
 * Collections are comprised of many queries which should be grouped together between parenthesis
 * All collectionQueryStrings should be joined together with 'and' to form a complete and final q string
 *
 * @param collections - The collection of queries to convert
 * @returns The q string
 */

const convertCollectionsToQ = (
  collections: Record<string, QueryCollection>
): string | undefined => {
  const activeCollections = Object.values(collections).filter(
    (collection) => collection.queries.length > 0
  )

  if (activeCollections.length === 0) return undefined

  const formattedCollections = activeCollections.reduce((acc, collection) => {
    const queryStringsForValue =
      collection.type !== 'global'
        ? convertQueriesToQ(collection.queries, collection.type)
        : undefined
    const queryGroup = queryStringsForValue
      ? `${queryStringsForValue}`
      : undefined

    if (queryGroup) {
      acc.push(queryGroup)
    }

    return acc
  }, [] as string[])

  return formattedCollections.length > 0
    ? formattedCollections.join(' and ')
    : undefined
}

const convertCollectionsToSearchValue = (
  collections: Record<string, QueryCollection>
): string | undefined => {
  const activeCollections = Object.values(collections).filter(
    (collection) => collection.queries.length > 0
  )

  if (activeCollections.length === 0) return undefined

  const globalCollection = activeCollections.find(
    (collection) => collection.type === 'global'
  )
  if (globalCollection) {
    return globalCollection.queries?.[0]?.queryValues?.[0]
  }

  return undefined
}

type SheetStoreMiddleware = [
  [
    'zustand/persist',
    {
      filters: Record<string, SheetFilterState>
      sheetQueryCollections: Record<string, SheetQueryCollectionState>
      queries: Record<string, SheetQueriesState>
    },
  ],
  ['zustand/immer', never],
]

// Create the store with persist, immer and devtools middleware
export const useSheetStore: UseBoundStore<
  Mutate<StoreApi<SheetStoreState>, SheetStoreMiddleware>
> = create<SheetStoreState, SheetStoreMiddleware>(
  persist(
    immer((set, get) => ({
      filters: {},
      sheetQueryCollections: {},
      queries: {},

      // Initialization function for a sheet
      initSheet: (sheetId: string) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
        }, false)
      },

      // Get filters for a specific sheet (pure selector)
      getFilters: (sheetId: string) => {
        const state = get()
        // Return default state if sheet doesn't exist
        return state.filters[sheetId] || DEFAULT_FILTER_STATE
      },

      getQueries: (sheetId: string) => {
        const state = get()
        return state.queries[sheetId] || DEFAULT_QUERIES_STATE
      },

      // Set individual filter properties
      setFilter: (sheetId: string, filter: ValidityFilter | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].filter = filter
        }, false)
      },

      setFilterField: (sheetId: string, filterField: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].filterField = filterField
        }, false)
      },

      setSearchValue: (sheetId: string, searchValue: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].searchValue = searchValue
        }, false)
      },

      setSearchField: (sheetId: string, searchField: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].searchField = searchField
        }, false)
      },

      setQ: (sheetId: string, q: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].q = q
        }, false)
      },

      setSortDirection: (
        sheetId: string,
        sortDirection: SortDirection | undefined
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].sortDirection = sortDirection
        }, false)
      },

      setSortField: (sheetId: string, sortField: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.filters[sheetId].sortField = sortField
        }, false)
      },

      // Update multiple filter properties at once
      updateFilters: (
        sheetId: string,
        partialFilters: Partial<SheetFilterState>
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          Object.assign(state.filters[sheetId], partialFilters)
        }, false)
      },

      // Reset filters for a specific sheet to defaults
      resetFilters: (sheetId: string) => {
        set((state) => {
          state.filters[sheetId] = { ...DEFAULT_FILTER_STATE }
        }, false)
      },

      // Get query sets for a specific sheet (pure selector)
      getQueryCollectionState: (sheetId: string) => {
        const state = get()
        return (
          state.sheetQueryCollections[sheetId] || DEFAULT_QUERY_COLLECTION_STATE
        )
      },

      setQueryCollectionState: (
        sheetId: string,
        queryCollectionState: SheetQueryCollectionState
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.sheetQueryCollections[sheetId] = queryCollectionState
        }, false)
      },

      createQueryCollection: (
        sheetId: string,
        query: Omit<SheetQuery, 'id'> | undefined,
        type: 'atomic' | 'advanced' | 'global'
      ) => {
        const collectionId = crypto.randomUUID()
        const queryId = crypto.randomUUID()
        const newQuery: SheetQuery | undefined = query
          ? {
              ...query,
              id: queryId,
              queryValues: query.queryValues || [],
            }
          : undefined
        const newCollection: QueryCollection = {
          id: collectionId,
          type,
          queries: newQuery ? [newQuery] : [],
        }
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.sheetQueryCollections[sheetId].queryCollections[collectionId] =
            newCollection
          state.filters[sheetId].q = convertCollectionsToQ(
            state.sheetQueryCollections[sheetId].queryCollections
          )
          state.filters[sheetId].searchValue = convertCollectionsToSearchValue(
            state.sheetQueryCollections[sheetId].queryCollections
          )
        }, false)
        return newCollection
      },

      updateQueryCollection: (
        sheetId: string,
        collectionId: string,
        updatedCollection: Partial<QueryCollection>
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          const collection =
            state.sheetQueryCollections[sheetId].queryCollections[collectionId]
          if (collection) {
            state.sheetQueryCollections[sheetId].queryCollections[
              collectionId
            ] = {
              ...collection,
              ...updatedCollection,
            }
            state.filters[sheetId].q = convertCollectionsToQ(
              state.sheetQueryCollections[sheetId].queryCollections
            )
            state.filters[sheetId].searchValue =
              convertCollectionsToSearchValue(
                state.sheetQueryCollections[sheetId].queryCollections
              )
          }
        }, false)
      },

      removeQueryCollection: (sheetId: string, collectionId: string) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          delete state.sheetQueryCollections[sheetId].queryCollections[
            collectionId
          ]
          state.filters[sheetId].q = convertCollectionsToQ(
            state.sheetQueryCollections[sheetId].queryCollections
          )
          state.filters[sheetId].searchValue = convertCollectionsToSearchValue(
            state.sheetQueryCollections[sheetId].queryCollections
          )
        }, false)
      },

      clearQueryCollections: (sheetId: string) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.sheetQueryCollections[sheetId].queryCollections = {}
          state.filters[sheetId].q = convertCollectionsToQ(
            state.sheetQueryCollections[sheetId].queryCollections
          )
          state.filters[sheetId].searchValue = convertCollectionsToSearchValue(
            state.sheetQueryCollections[sheetId].queryCollections
          )
          state.queries[sheetId].activeQueryId = undefined
          state.sheetQueryCollections[sheetId].activeQueryCollectionId =
            undefined
        }, false)
      },

      addQueryCollectionQuery: (
        sheetId: string,
        collectionId: string,
        query: Partial<Omit<SheetQuery, 'id'>>
      ) => {
        const queryId = crypto.randomUUID()
        const newQuery = {
          ...query,
          id: queryId,
          queryValues: query.queryValues || [],
        } as SheetQuery
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.sheetQueryCollections[sheetId].queryCollections[
            collectionId
          ].queries.push(newQuery)
        }, false)
        return newQuery
      },

      updateQueryCollectionQuery: (
        sheetId: string,
        collectionId: string,
        query: SheetQuery
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          const collection =
            state.sheetQueryCollections[sheetId].queryCollections[collectionId]
          if (collection) {
            const changedQueryIndex = collection.queries.findIndex(
              (q) => q.id === query.id
            )
            if (changedQueryIndex !== -1) {
              collection.queries[changedQueryIndex] = {
                ...collection.queries[changedQueryIndex],
                ...query,
              }
            }
          }
          state.filters[sheetId].q = convertCollectionsToQ(
            state.sheetQueryCollections[sheetId].queryCollections
          )
          state.filters[sheetId].searchValue = convertCollectionsToSearchValue(
            state.sheetQueryCollections[sheetId].queryCollections
          )
        }, false)
      },

      removeQueryCollectionQuery: (
        sheetId: string,
        collectionId: string,
        queryId: string
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          const collection =
            state.sheetQueryCollections[sheetId].queryCollections[collectionId]
          if (collection) {
            const changedQueryIndex = collection.queries.findIndex(
              (q) => q.id === queryId
            )
            if (changedQueryIndex !== -1) {
              collection.queries.splice(changedQueryIndex, 1)
            }
          }
          state.filters[sheetId].q = convertCollectionsToQ(
            state.sheetQueryCollections[sheetId].queryCollections
          )
        }, false)
      },

      setActiveQuery: (sheetId: string, queryId: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.queries[sheetId].activeQueryId = queryId
        }, false)
      },

      setActiveView: (sheetId: string, viewId: string | undefined) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.queries[sheetId].activeViewId = viewId
        }, false)
      },

      setActiveQueryCollection: (
        sheetId: string,
        collectionId: string | undefined
      ) => {
        set((state) => {
          ensureSheetExists(state, sheetId)
          state.sheetQueryCollections[sheetId].activeQueryCollectionId =
            collectionId
        }, false)
      },
    })),
    {
      name: 'sheet-filters-storage',
      partialize: (state) => ({
        filters: state.filters,
        sheetQueryCollections: state.sheetQueryCollections,
        queries: state.queries,
      }),
    }
  )
)
